import {
  cacheKeys,
  FIND_GROUP_ASSIGNMENTS,
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AssignmentModal } from '../../shared/components/assignment/assignmentModal';
import { EditGroupAccountsList } from './editGroupAccountsList';
import { EditGroupAssetsList } from './editGroupAssetsList';
import { EditGroupRoutinesList } from './editGroupRoutinesList';

const ASSIGN_TO_GROUP = `
  mutation AssignToGroup($input: AssignToGroupInput!) {
    group {
      assignToGroup(input: $input) {
        success
        message
      }
    }
  }
`;

export enum AssignmentType {
  Routines = 'routines',
  Assets = 'assets',
  People = 'people',
}

type GroupAssignmentFormProps = {
  groupId: string;
  title: string;
  assignmentType: AssignmentType;
  onComplete: () => void;
};

export const GroupAssignmentForm = ({
  title,
  onComplete,
  groupId,
  assignmentType,
}: GroupAssignmentFormProps) => {
  const { t } = useTranslation();
  const currentWorkspace = useCurrentWorkspace(true);
  const queryClient = useQueryClient();

  const { data, isPending } = useCustomQuery({
    query: FIND_GROUP_ASSIGNMENTS,
    cacheKey: ['group', 'assignments', groupId],
    variables: { groupId },
  });
  const found = data?.group?.findGroupAssignments;

  const { mutateAsync: assignToGroup } = useCustomMutation({
    query: ASSIGN_TO_GROUP,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: cacheKeys('group').all });

  const save = (field: 'assetIds' | 'accountIds' | 'routineIds') =>
    async ({ selected }: { selected: string[] }) => {
      await assignToGroup(
        { input: { [field]: selected, groupId, workspaceId: currentWorkspace!.id } },
        { onSettled: invalidate }
      );
    };

  return (
    <AssignmentModal
      title={title}
      isLoading={isPending || !currentWorkspace?.id}
      initialTabKey={assignmentType}
      onComplete={onComplete}
      tabs={[
        {
          key: AssignmentType.Assets,
          label: t('groups.assignments.assets'),
          searchPlaceholder: t('groups.assignments.assetSearchPlaceholder'),
          assignments: found?.assets,
          onSave: save('assetIds'),
          renderList: (p) => (
            <EditGroupAssetsList
              workspace={currentWorkspace}
              groupId={groupId}
              maxHeight={p.maxHeight}
              keyword={p.keyword}
              assignments={p.assignments}
              updateAssignments={p.onAssignmentsChange}
            />
          ),
        },
        {
          key: AssignmentType.People,
          label: t('groups.assignments.people'),
          searchPlaceholder: t('groups.assignments.accountSearchPlaceholder'),
          assignments: found?.accounts,
          onSave: save('accountIds'),
          renderList: (p) => (
            <EditGroupAccountsList
              workspace={currentWorkspace}
              groupId={groupId}
              maxHeight={p.maxHeight}
              keyword={p.keyword}
              assignments={p.assignments}
              updateAssignments={p.onAssignmentsChange}
            />
          ),
        },
        {
          key: AssignmentType.Routines,
          label: t('groups.assignments.routines'),
          searchPlaceholder: t('groups.assignments.routineSearchPlaceholder'),
          assignments: found?.routines,
          onSave: save('routineIds'),
          renderList: (p) => (
            <EditGroupRoutinesList
              workspace={currentWorkspace}
              groupId={groupId}
              maxHeight={p.maxHeight}
              keyword={p.keyword}
              assignments={p.assignments}
              updateAssignments={p.onAssignmentsChange}
            />
          ),
        },
      ]}
    />
  );
};
